/**
 * routes.tsx — the web app's nested route tree (replaces the inline
 * `<Routes>` previously in `main.tsx`).
 *
 * Shape:
 *   /login, /auth/callback        public auth routes
 *   /                             → /dashboard
 *   <WebShellLayout> (AuthGuard + shell)
 *     /dashboard                  the home grid
 *     /winning/search-creds       started (FeatureRoute-gated)
 *     /research/imports           started (FeatureRoute-gated)
 *     /research/imports/:importId inherits my-imports' flag
 *     …every other leftMenuModel item path  off by default, reachable only
 *                                            in preview, renders ComingSoonPage
 *   *                             → /dashboard
 *
 * Model-driven route generation (PR #110 fix — orphan routes): EVERY
 * `leftMenuModel` item path gets a route, generated from the model itself
 * so nav and routing can NOT drift. Items with a dedicated page component
 * (the two started features) are declared in `PAGE_OVERRIDES`; every other
 * item falls back to a generic `<ComingSoonPage>`. Each generated route is
 * wrapped in `<FeatureRoute sectionId pageId>` so a direct URL hit on an
 * off feature redirects to /dashboard for a normal user (and renders the
 * placeholder in preview) — never falling through to the catch-all.
 *
 * The catch-all `*` → `/dashboard` is reserved for genuinely unknown paths
 * (typos, stale bookmarks), NOT for known-but-unbuilt nav items.
 *
 * The path↔route invariant is asserted in routes.test.tsx: every model
 * path matches a defined route, and no model item escapes to the catch-all.
 */
import type { ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { activeFlags, isPageReleased, resolveStage } from "../config/featureFlags";
import { usePreview } from "../features/PreviewProvider";
import { pageRevealedByMode } from "../features/useFeatureAccess";
import { ComingSoonPage } from "../routes/_placeholder/ComingSoonPage";
import { AdministrationPage } from "../routes/operations/AdministrationPage";
import { AuthCallback } from "../routes/auth-callback/AuthCallback";
import { AgendaForm } from "../routes/client-engagement/AgendaForm";
import { SearchDetailLanding } from "../routes/client-engagement/searches/SearchDetailLanding";
import { SearchDetailPage } from "../routes/client-engagement/searches/SearchDetailPage";
import { SearchesPage } from "../routes/client-engagement/searches/SearchesPage";
import { EmailTemplateEditor } from "../routes/closing/EmailTemplateEditor";
import { SendClosingEmail } from "../routes/closing/SendClosingEmail";
import { Dashboard } from "../routes/dashboard/Dashboard";
import { Login } from "../routes/login/Login";
import { SearchesImport } from "../routes/operations/SearchesImport";
import { SequencesPage } from "../routes/outreach/sequences/SequencesPage";
import { ContactDetailPage } from "../routes/people/ContactDetailPage";
import { PrivacyPage } from "../routes/privacy/PrivacyPage";
import { AiReviewPage } from "../routes/research/ai-review/AiReviewPage";
import { BabyChopsV2Tool } from "../routes/research/BabyChopsV2Tool";
import { ChopbookTool } from "../routes/research/ChopbookTool";
import { BctPage } from "../routes/research/BctPage";
import { CombineFilterPage } from "../routes/research/combine-filter/CombineFilterPage";
import { CompanyListDetailPage } from "../routes/research/CompanyListDetailPage";
import { CompanyListsPage } from "../routes/research/CompanyListsPage";
import { COMPANY_LIST_ROUTE, COMPANY_PAGE_ROUTE } from "../features/company-lists/paths";
import { CompanyRecordPage } from "../routes/companies/CompanyRecordPage";
import { ImportsPage } from "../routes/research/ImportsPage";
import { NewImportPage } from "../routes/research/new-import/NewImportPage";
import { ImportDetailPage } from "../routes/research/ImportDetailPage";
import { RocketReachTool } from "../routes/research/RocketReachTool";
import { SlideCreator } from "../routes/slide-creator/SlideCreator";
import { SyncContacts } from "../routes/sync-phone/SyncContacts";
import { WebShellLayout } from "../shell/WebShellLayout";
import { LEFT_MENU_SECTIONS, type LeftMenuItem } from "../shell/leftMenuModel";
import { PERSON_PAGE_ROUTE, SEARCH_DETAIL_ROUTE } from "./paths";
import { AdminRoute } from "./AdminRoute";
import { FeatureRoute } from "./FeatureRoute";

/** A gated route element: guard + content. */
function Gated({
  sectionId,
  pageId,
  children,
}: {
  sectionId: string;
  pageId: string;
  children: ReactNode;
}) {
  return (
    <FeatureRoute sectionId={sectionId} pageId={pageId}>
      {children}
    </FeatureRoute>
  );
}

/**
 * BCT (Backchannel Tree) content gate.
 *
 * `FeatureRoute` already decides whether the user may be HERE at all. This
 * inner gate decides whether they get the real form or a placeholder, and it
 * exists because for a year the two answers differed: the nav item was
 * visible while the page deliberately stayed Coming Soon, revealed only by a
 * preview mode (`bct` is a Phase 2 id in `PHASE_PAGE_IDS`, so `?preview=2`,
 * `?preview=3` and `?preview=all` revealed it; `?preview=1` did not).
 *
 * **Released is now the first clause, and that is the whole 2026-08-10 fix.**
 * With `bct` flipped on in `featureFlags`, the preview-only test would have
 * kept returning false for a normal user (`previewMode === 0`), so a released
 * page would have rendered `<ComingSoonPage>` to every recruiter — a silent
 * regression that `warnIfReleasedWithoutOverride` cannot catch, because `bct`
 * DOES have a `PAGE_OVERRIDES` entry; the entry just pointed at a component
 * that renders the placeholder. Reading the flag directly (rather than
 * deleting the gate) keeps the preview path working for whichever stage
 * releases last.
 *
 * Still independent of the backend `ENABLE_BCT` env var, which gates whether
 * the API answers at all. As of this release that is true on every stage
 * (`infra/api-stack.ts`) — it had to move in lockstep, or prod recruiters
 * would have reached a form whose every call 404s.
 */
function BctRoute() {
  const { previewMode } = usePreview();
  const released = isPageReleased(activeFlags(), "linkedin", "bct");
  return released || pageRevealedByMode(previewMode, "bct") ? (
    <BctPage />
  ) : (
    <ComingSoonPage title="Company Map" />
  );
}

/**
 * Retired paths that must not dead-click. Each renders a `<Navigate replace>`
 * to its successor, registered alongside the model-generated routes so a
 * stale bookmark lands on the live page instead of falling through the
 * catch-all to `/dashboard`.
 *
 * Deliberately NOT wrapped in `<Gated>`: a redirect carries no content to
 * protect, and the destination is itself gated, so gating the hop as well
 * would only mean a user who cannot see the target gets bounced to
 * `/dashboard` from the redirect rather than from the target — the same
 * outcome by a more confusing path. `replace` so Back does not trap the user
 * in a redirect loop.
 */
const LEGACY_REDIRECTS: Array<{ from: string; to: string }> = [
  // Administration sub-pages → unified Staff Members tabbed page (2026-10-28).
  // The three separate sidebar items (People & Access, Activity log, Admins)
  // were collapsed into tabs on /operations/administration/staff-members.
  { from: "/operations/administration/people", to: "/operations/administration/staff-members?tab=people" },
  { from: "/operations/administration/activity", to: "/operations/administration/staff-members?tab=activity" },
  { from: "/operations/administration/admins", to: "/operations/administration/staff-members?tab=admins" },
  // Baby Chops v1 → v2. The v2 page replaced the v1 menu item outright
  // (2026-08-10 on dev, 2026-09-23 on
  // prod), so v1's path redirects rather than 404s: the Drive-era page was
  // linked from Slack and bookmarked by the team, and a bookmark that
  // silently lands on /dashboard reads as "the tool is gone". Unconditional
  // again since 2026-09-23 — the production freeze's prod-only v1 page is
  // gone, so no route competes for this path on any stage.
  {
    from: "/research/baby-chops",
    to: "/research/baby-chops-v2",
  },
  {
    // Backchannel Tree -> Company Map rename (2026-08-14, Jared). Label, URL
    // and generated filenames changed; internal ids/tables keep `bct`.
    from: "/research/backchannel-tree",
    to: "/research/company-map",
  },
];

/**
 * Redirect that carries the query string across the hop. A legacy
 * "Feed into BCT" deep link (`/research/backchannel-tree?importId=…`) must
 * land with its pre-selection intact — a bare `<Navigate to={path}>` would
 * silently drop `?importId=` / `?preview=`.
 */
function LegacyRedirect({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search }} replace />;
}

/**
 * Item ids that render a dedicated page component instead of the generic
 * ComingSoon placeholder. Everything else falls back to ComingSoonPage —
 * so a newly-added nav item is automatically routable (preview-visible)
 * even before its real page exists.
 *
 * NOTE: `closing-email-template` is no longer a menu item (demoted to a
 * secondary in-page button on 2026-06-18 — Jared feedback), so it has no
 * generated route from the menu sweep. Its route is registered under
 * `SUB_RESOURCE_ROUTES` below, inheriting the `closing-email` flag so
 * anyone who can use Send Search Closing Email can also click the "Edit
 * templates" companion. The PAGE_OVERRIDES entry would be ignored for an
 * id with no matching menu item, so we omit it here on purpose.
 */
const PAGE_OVERRIDES: Record<string, ReactNode> = {
  "search-creds": <SlideCreator />,
  "closing-email": <SendClosingEmail />,
  "agenda-gpt": <AgendaForm />,
  "tool-rocketreach": <RocketReachTool />,
  "tool-babychops-v2": <BabyChopsV2Tool />,
  "tool-chopbook": <ChopbookTool />,
  "my-imports": <ImportsPage />,
  // Released on both stages 2026-10-07 (it was phase 3, `?preview=3`, from
  // epic 2026-09-22 sub-task 04 until then).
  "combine-filter": <CombineFilterPage />,
  "dash-searches": <SearchesPage />,
  // The sidebar's parameter-less Search Detail entry. A detail page needs a
  // subject, so this resolves one (last-viewed search, else a picker prompt)
  // rather than rendering an empty report. See the component for why the nav
  // item is kept at all.
  "dash-detail": <SearchDetailLanding />,
  "ai-review": <AiReviewPage />,
  "company-lists": <CompanyListsPage />,
  "searches-import": <SearchesImport />,
  "sync-phone": <SyncContacts />,
  "seq-info": <SequencesPage />,
  // groupHeader `administration` still generates a route at
  // /operations/administration; redirect it to the canonical staff-members path
  // so direct-URL hits and old bookmarks land on the tabbed page.
  administration: <LegacyRedirect to="/operations/administration/staff-members" />,
  "staff-members": (
    <AdminRoute>
      <AdministrationPage />
    </AdminRoute>
  ),
  // Coming Soon by default in every stage; see `BctRoute` above for the
  // preview-tier reveal.
  bct: <BctRoute />,
};

/**
 * Extra dynamic sub-resource routes that inherit a parent item's flag.
 * These are NOT in the flat nav model (the nav points at the list page)
 * but must still resolve to a gated route. Keyed by the path; each carries
 * the parent item's section/page id so it inherits the same flag.
 */
const SUB_RESOURCE_ROUTES: Array<{
  path: string;
  sectionId: string;
  pageId: string;
  element: ReactNode;
}> = [
  {
    // New Import wizard — declared BEFORE the `:importId` route so the literal
    // `/new` segment wins over the param (react-router ranks static > dynamic,
    // but keep them adjacent for clarity). Inherits my-imports' feature flag.
    path: "/research/imports/new",
    sectionId: "linkedin",
    pageId: "my-imports",
    element: <NewImportPage />,
  },
  {
    path: "/research/imports/:importId",
    sectionId: "linkedin",
    pageId: "my-imports",
    element: <ImportDetailPage />,
  },
  {
    // Contact detail — the person side panel's standalone page (epic
    // 2026-09-21). Not a menu item: reached from the panel's "Open as page" and
    // from shared links. Gated by its own `person-panel` flag under the
    // Research section — released on both stages since 2026-09-23; a build
    // where the flag is off bounces this path to /dashboard.
    path: PERSON_PAGE_ROUTE,
    sectionId: "linkedin",
    pageId: "person-panel",
    element: <ContactDetailPage />,
  },
  {
    path: COMPANY_LIST_ROUTE,
    sectionId: "linkedin",
    pageId: "company-lists",
    element: <CompanyListDetailPage />,
  },
  {
    // The company record ("Open as page" from the company panel). Same gate
    // as the lists: phase 3, off on both stages until they are released.
    path: COMPANY_PAGE_ROUTE,
    sectionId: "linkedin",
    pageId: "company-lists",
    element: <CompanyRecordPage />,
  },
  {
    // Search Detail. Moved with its parent from `/operations/searches/:searchId`
    // to the Client Engagement section on 2026-08-05. Declared AFTER the menu
    // sweep that registers the literal `/client-engagement/searches/detail`
    // sidebar path; react-router ranks static segments above dynamic ones, so
    // the two coexist regardless of order (Thrive search ids are numeric, so
    // `detail` can never be a real id).
    //
    // Inherits `dash-searches` rather than carrying `dash-detail`: a row click
    // from the list must land somewhere reachable, so the detail sub-route is
    // gated by the list that links to it.
    //
    // The pattern comes from `paths.ts`, which also exports the
    // `searchDetailPath()` builder every link site uses — so a rename here
    // cannot leave a link pointing at a route that no longer exists.
    path: SEARCH_DETAIL_ROUTE,
    sectionId: "client-engagement",
    pageId: "dash-searches",
    element: <SearchDetailPage />,
  },
  {
    // Closing Email Template Editor — demoted from a top-level sidebar
    // item to a secondary "Edit templates" button inside Send Search
    // Closing Email on 2026-06-18 (Jared feedback). Gated by the parent
    // page's flag (`closing-email`) so anyone who can use the closing
    // flow can also reach the companion editor; the route stays
    // navigable via the in-page link, just not from the sidebar.
    path: "/closing/email-template",
    sectionId: "closing",
    pageId: "closing-email",
    element: <EmailTemplateEditor />,
  },
];

/** Flatten the nav model into a single list of routable items. */
function allMenuItems(): LeftMenuItem[] {
  return LEFT_MENU_SECTIONS.flatMap((section) => section.items);
}

function elementForItem(item: LeftMenuItem): ReactNode {
  return PAGE_OVERRIDES[item.id] ?? <ComingSoonPage title={item.label} />;
}

/**
 * Page ids that are *intentionally* released-with-placeholder — flag on,
 * no `PAGE_OVERRIDES` entry, falls back to `<ComingSoonPage>` on purpose.
 * Listed here so the dev-time warning below does NOT fire on them (they
 * are acknowledged structural placeholders, not silent gaps).
 *
 * Currently empty — PR #240 wired `seq-info` to the real `<SequencesPage/>`
 * component (see `PAGE_OVERRIDES` above), so it no longer qualifies. The
 * post-merge cleanup removed the stale entry per the doc rule below.
 *
 * Remove an id from this set the moment its real component is registered
 * in `PAGE_OVERRIDES` — leaving a stale acknowledgement defeats the guard.
 *
 * If empty for long, that is itself a healthy signal (no released-with-
 * placeholder gaps); keep the set + its exported `_IDS` alias around so
 * future regressions can re-introduce an entry without re-plumbing.
 */
const ACKNOWLEDGED_PLACEHOLDERS: ReadonlySet<string> = new Set([]);

/**
 * Dev-time guard against "released-with-placeholder" pages — a page id
 * whose flag is on (would render to a real recruiter) but whose route
 * has no entry in `PAGE_OVERRIDES`, so it silently falls back to
 * `<ComingSoonPage>`. Real recruiters then see "coming soon" where the
 * curated surface promised a feature.
 *
 * Build-time fail is harder than a warning (would require rejecting the
 * Vite bundle); the warning is paired with the vitest unit-test
 * guardrail in `featureFlags.guardrail.test.ts` which fails CI on the
 * same condition for PROD_FLAGS. This warning catches the SAME class of
 * mistake at dev runtime for the resolved (dev or local) stage's flags.
 *
 * Stage-gated: prod builds stay silent (the prod bundle should not log
 * developer warnings to recruiters' devtools). Per-id dedup via a Set
 * so a re-render storm cannot spam the console.
 */
const warnedReleasedWithoutOverride = new Set<string>();

function warnIfReleasedWithoutOverride(items: LeftMenuItem[]): void {
  // Capture once: cheaper than calling twice AND removes the readability
  // trip-up Agent C iter-1 finding #4 flagged (the warning text used to
  // re-invoke `resolveStage()` for interpolation).
  const stage = resolveStage();
  if (stage === "prod") return;
  const flags = activeFlags();
  for (const item of items) {
    if (!isPageReleased(flags, item.sectionId, item.id)) continue;
    if (PAGE_OVERRIDES[item.id] !== undefined) continue;
    if (ACKNOWLEDGED_PLACEHOLDERS.has(item.id)) continue;
    if (warnedReleasedWithoutOverride.has(item.id)) continue;
    warnedReleasedWithoutOverride.add(item.id);
    // Intentional dev-time warning surfaced to the operator console.
    // Gated by the prod-stage check above so the prod bundle never emits
    // it (verify: `grep "\[FeatureFlags\]" dist/assets/*.js` should be
    // empty under VITE_APP_STAGE=prod).
    console.warn(
      `[FeatureFlags] page "${item.id}" is flagged released in ${stage} but no PAGE_OVERRIDES entry exists; falling back to <ComingSoonPage>. Either flip the flag back OR register the component in PAGE_OVERRIDES.`
    );
  }
}

/**
 * Exposed so the `ACKNOWLEDGED_PLACEHOLDERS ∩ PROD_FLAGS.pages-on === ∅`
 * symmetry guardrail in `featureFlags.guardrail.test.ts` can introspect
 * the acknowledged set without re-declaring it. Read-only mirror.
 */
export const ACKNOWLEDGED_PLACEHOLDER_IDS: ReadonlySet<string> = ACKNOWLEDGED_PLACEHOLDERS;

/**
 * Reset the dev-time warning dedup cache. Test-only escape hatch so a
 * suite that asserts the warning fires can reset state between cases —
 * production code never calls this.
 */
export function __resetReleasedWithoutOverrideWarningsForTests(): void {
  warnedReleasedWithoutOverride.clear();
}

/** Exported solely so the unit-test guardrail can introspect it. */
export const PAGE_OVERRIDE_IDS: ReadonlySet<string> = new Set(Object.keys(PAGE_OVERRIDES));

/**
 * Page ids that own at least one `SUB_RESOURCE_ROUTES` entry — i.e. a real
 * component is wired for them OUTSIDE the menu sweep.
 *
 * Exported for the `featureFlags.guardrail` PROD interlock. That guard asks
 * "does every released prod page id render something real?", and until
 * 2026-09-23 `PAGE_OVERRIDES` alone answered it, because every released id was
 * a menu item. `person-panel` (released with the rest of phase 2 that day) is
 * not: it has no sidebar row, so the menu sweep generates no route for it and
 * a `PAGE_OVERRIDES` entry would be inert — its page lives here, at
 * `PERSON_PAGE_ROUTE`. Without this mirror the guard would demand a dead entry.
 */
export const SUB_RESOURCE_ROUTE_PAGE_IDS: ReadonlySet<string> = new Set(
  SUB_RESOURCE_ROUTES.map((r) => r.pageId),
);

/**
 * The section each sub-resource route resolves its flag under — the other
 * half of `pageVisible(flags, mode, sectionId, pageId)` for ids the menu
 * sweep never sees. The guardrail reads it to check that a released page is
 * not sitting under a section whose own flag is off (/pr-review #963).
 */
export const SUB_RESOURCE_SECTION_IDS: ReadonlyMap<string, string> = new Map(
  SUB_RESOURCE_ROUTES.map((r) => [r.pageId, r.sectionId] as const),
);

/**
 * Page ids that are neither menu items nor sub-resource routes: a gated
 * feature rendered INSIDE another page's route, behind a `pageVisible(...)`
 * check at its call site. The third shape a released id can take, and the
 * one the two sets above cannot describe — a `PAGE_OVERRIDES` entry would be
 * inert (no menu sweep routes it) and a `SUB_RESOURCE_ROUTES` entry would
 * mint a path nothing links to.
 *
 * Each entry names the HOST whose route renders it and the section both
 * resolve under, so the `featureFlags.guardrail` PROD interlock can still ask
 * its question — "does this released id render something real?" — as "is its
 * host released on prod too?". A tab released on prod inside a page that is
 * not would be reachable from nowhere, which is the silent fallback the guard
 * exists to catch.
 *
 * `search-candidates` (2026-10-06): the Candidates tab of Search Detail,
 * `SearchDetailPage.tsx`, selected with `?tab=candidates` on the
 * `dash-searches` sub-resource route. `bullseye` (registered 2026-10-07, with
 * its release on prod): the Bullseye tab of the same page, `?tab=bullseye`.
 * `thrive-search-import` (2026-10-19, unreleased): the Source step of the New
 * Import wizard (`NewImportPage.tsx`), the `my-imports` sub-resource route
 * `/research/imports/new`. `four-reports` (2026-10-23) left with its flag
 * when the four candidate report kinds were released (2026-10-26, #1092).
 */
export const IN_PAGE_FEATURE_IDS: ReadonlyMap<string, { sectionId: string; hostPageId: string }> =
  new Map([
    ["search-candidates", { sectionId: "client-engagement", hostPageId: "dash-searches" }],
    ["bullseye", { sectionId: "client-engagement", hostPageId: "dash-searches" }],
    ["thrive-search-import", { sectionId: "linkedin", hostPageId: "my-imports" }],
  ]);

/**
 * Exported solely so the stage-disjointness invariant test can introspect
 * the redirect list (PR #821 iter-1, Agent A finding 2): the v1
 * `/research/baby-chops` path must belong to exactly ONE owner per stage —
 * the redirect (dev/local) or the menu-generated route (prod) — and the two
 * owners live in two modules whose stage checks could drift apart silently.
 * `routes.test.tsx` re-imports both modules per stage and asserts
 * `LEGACY_REDIRECT_PATHS ∩ menu paths = ∅`. Read-only mirror.
 */
export const LEGACY_REDIRECT_PATHS: ReadonlySet<string> = new Set(
  LEGACY_REDIRECTS.map((r) => r.from),
);

export function AppRoutes() {
  const items = allMenuItems();
  warnIfReleasedWithoutOverride(items);

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* /privacy — public, unauthenticated. Registered above
          <WebShellLayout> so it bypasses AuthGuard. Exists so Google's
          OAuth consent screen can validate a Privacy Policy URL at
          https://app.prod.nu-assist.com/privacy. */}
      <Route path="/privacy" element={<PrivacyPage />} />

      <Route element={<WebShellLayout />}>
        <Route path="/dashboard" element={<Dashboard />} />

        {/* One gated route per nav model item — generated from the model
            so nav paths and routes can never drift (no orphan paths). */}
        {items.map((item) => (
          <Route
            key={item.path}
            path={item.path}
            element={
              <Gated sectionId={item.sectionId} pageId={item.id}>
                {elementForItem(item)}
              </Gated>
            }
          />
        ))}

        {/* Retired paths → their successors. Registered before the catch-all
            so a stale bookmark reaches the live page. */}
        {LEGACY_REDIRECTS.map((r) => (
          <Route key={r.from} path={r.from} element={<LegacyRedirect to={r.to} />} />
        ))}

        {/* Dynamic sub-resource routes that inherit a parent's flag. */}
        {SUB_RESOURCE_ROUTES.map((r) => (
          <Route
            key={r.path}
            path={r.path}
            element={
              <Gated sectionId={r.sectionId} pageId={r.pageId}>
                {r.element}
              </Gated>
            }
          />
        ))}
      </Route>

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
